// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators compare each service across twelve months and a
 * pinned yearly total. Use a CSS grid so those columns remain aligned with the
 * Metrics dashboard at every width. Service names come from backend records and
 * may not follow provider naming conventions; long model ids have no spaces, so
 * wrap them with break-all to keep the full value visible, and fall back to
 * plain text when the optional first-word chip is not useful.
 */
import { Loader2 } from 'lucide-react'
import { CostRollup, YearlyCostsResponse } from '../../types'

const GRID = 'grid grid-cols-[230px_repeat(12,minmax(0,1fr))_100px] px-2'
const CURRENT_TINT = 'rgba(37,99,235,0.045)'

const formatUSD = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)

const monthName = (month: number) =>
  new Date(2024, month - 1).toLocaleString('default', { month: 'short' })

const rollupsFor = (costs: YearlyCostsResponse, costType: string): CostRollup[] =>
  Array.isArray(costs[costType]) ? costs[costType] : []

const monthTotal = (costs: YearlyCostsResponse, costType: string, month: number): number => {
  const rollup = rollupsFor(costs, costType).find(r => r?.month === month)
  return rollup ? parseFloat(rollup.total_usd) : 0
}

const yearTotal = (costs: YearlyCostsResponse, costType: string): number =>
  rollupsFor(costs, costType).reduce((sum, r) => sum + (r?.total_usd ? parseFloat(r.total_usd) : 0), 0)

const displayName = (costs: YearlyCostsResponse, costType: string): string =>
  rollupsFor(costs, costType)[0]?.cost_display_name
  ?? costType.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()

interface CostsTableProps {
  costs: YearlyCostsResponse
  isLoading?: boolean
  year: number
  /** 1-based month to highlight, only set when viewing the current calendar year */
  currentMonth: number | null
}

export function CostsTable({ costs, isLoading, year, currentMonth }: CostsTableProps) {
  const months = Array.from({ length: 12 }, (_, i) => i + 1)
  const costTypes = Object.keys(costs)
  const tint = (month: number) =>
    month === currentMonth && costTypes.length > 0 ? { background: CURRENT_TINT } : undefined

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="flex items-center justify-between border-b border-[#eef2f6] px-6 py-4">
        <div className="text-sm font-semibold tracking-[-0.01em]">Cost breakdown by service</div>
        <div className="text-xs text-slate-400">All amounts in USD</div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-12">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          <span className="text-[13px] text-slate-500">Loading costs…</span>
        </div>
      ) : costTypes.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="text-[13.5px] font-[550] text-slate-700">No cost data for {year}</div>
          <div className="mt-1 text-[12.5px] text-slate-400">
            Costs will appear here once services record usage in this year.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[1100px] tabular-nums">
            <div className={`${GRID} items-center border-b border-[#eef2f6] bg-[#fafbfc]`}>
              <div className="px-4 py-2.5 text-[11.5px] font-semibold text-slate-500">Service</div>
              {months.map(month => (
                <div
                  key={month}
                  className={`px-2.5 py-2.5 text-right text-[11.5px] font-semibold ${
                    month === currentMonth ? 'text-[#2563eb]' : 'text-slate-500'
                  }`}
                >
                  {monthName(month)}
                </div>
              ))}
              <div className="flex h-full items-center justify-end bg-[#f4f6f9] px-4 text-[11.5px] font-semibold text-slate-700">
                Total
              </div>
            </div>

            {costTypes.map(costType => {
              const name = displayName(costs, costType)
              const [provider, ...rest] = name.split(/\s+/)
              const shortName = rest.join(' ')
              return (
                <div
                  key={costType}
                  className={`${GRID} items-stretch border-b border-slate-100 transition-colors duration-100 hover:bg-slate-50`}
                >
                  <div className="flex min-w-0 items-center gap-2 px-4 py-[11px]">
                    {shortName ? (
                      <>
                        <span className="flex-none rounded-[5px] border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-[650] uppercase tracking-[0.04em] text-slate-600">
                          {provider}
                        </span>
                        <span className="break-all text-[12.5px] font-[550] leading-[1.35] text-slate-800">{shortName}</span>
                      </>
                    ) : (
                      <span className="break-all text-[12.5px] font-[550] leading-[1.35] text-slate-800">{name}</span>
                    )}
                  </div>
                  {months.map(month => {
                    const value = monthTotal(costs, costType, month)
                    return (
                      <div
                        key={month}
                        style={tint(month)}
                        className={`flex items-center justify-end px-2.5 py-[11px] text-[12.5px] ${
                          value > 0 ? 'font-[550] text-slate-800' : 'text-slate-300'
                        }`}
                      >
                        {value > 0 ? formatUSD(value) : '—'}
                      </div>
                    )
                  })}
                  <div className="flex items-center justify-end bg-slate-50 px-4 py-[11px] text-[12.5px] font-semibold text-slate-900">
                    {formatUSD(yearTotal(costs, costType))}
                  </div>
                </div>
              )
            })}

            <div className={`${GRID} items-stretch border-t border-slate-200 bg-[#fafbfc]`}>
              <div className="flex items-center px-4 py-3 text-[12.5px] font-[650] text-slate-900">All services</div>
              {months.map(month => {
                const value = costTypes.reduce((sum, type) => sum + monthTotal(costs, type, month), 0)
                return (
                  <div
                    key={month}
                    style={tint(month)}
                    className={`flex items-center justify-end px-2.5 py-3 text-[12.5px] font-semibold ${
                      value > 0 ? 'text-slate-900' : 'text-slate-300'
                    }`}
                  >
                    {value > 0 ? formatUSD(value) : '—'}
                  </div>
                )
              })}
              <div className="flex items-center justify-end bg-[#f4f6f9] px-4 py-3 text-[13px] font-bold text-slate-900">
                {formatUSD(costTypes.reduce((sum, type) => sum + yearTotal(costs, type), 0))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
